// Dependencies
import {
  FC,
  useState,
  useCallback,
  SyntheticEvent,
  MouseEventHandler,
} from "react";
import { useSnackbar } from "notistack";
import { useMutation, useQuery, useQueryClient } from "react-query";

// Components
import { Tab, Tabs } from "@mui/material";

// api
import { getNotifyList, readNotify } from "~/services/notification";

// Types
import { QUERY_KEY } from "~/enums";
import { TQueryError } from "~/types";

// Styles
import {
  StyledLabel,
  StyledHeader,
  StyledWrapper,
  StyledNotifyItem,
  StyledNotifyList,
  StyledMarkAsRead,
} from "./Notification.styled";

interface NotificationProps {
  open: boolean;
  anchorEl: null | HTMLElement;
  id: string;
  onClose: MouseEventHandler;
}

const tabs = [
  { label: "All", value: "1" },
  // { label: "Wallet", value: "2" },
  // { label: "Transaction", value: "3" },
  // { label: "Others", value: "4" },
];

const Notification: FC<NotificationProps> = (props) => {
  const [tabValue, setTabValue] = useState<string>("1");
  const [listTop, setListTop] = useState<number>(0);

  const listRef = useCallback((node: HTMLUListElement) => {
    if (node !== null) {
      setListTop(node.getBoundingClientRect().top);
    }
  }, []);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  let focusTime: any = null;

  // Query
  const listNotifyQuery = useQuery({
    queryKey: QUERY_KEY.NOTIFY.NOTIFY_LIST,
    queryFn: getNotifyList,
  });

  const notifyList = listNotifyQuery.data || [];

  // Mutation
  const readNotifyMutation = useMutation({
    mutationFn: readNotify,
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    onSuccess() {
      queryClient.invalidateQueries(QUERY_KEY.NOTIFY.NOTIFY_LIST);
      queryClient.invalidateQueries(QUERY_KEY.NOTIFY.NOTIFY_TOTAL);
    },
  });

  // method
  const handleChangeTab = (event: SyntheticEvent, value: string) => {
    setTabValue(value);
  };

  const handleReadNotify = (notiId: number, isRead: boolean) => {
    if (!isRead) {
      readNotifyMutation.mutate(notiId);
    }
  };

  /**when user hover this notify more than 2s, set it as be read */
  const handleFocusReadNotify = (notiId: number, isRead: boolean) => {
    if (!isRead) {
      focusTime = setTimeout(() => {
        readNotifyMutation.mutate(notiId);
      }, 2000);
    }
  };

  const handleCancelReadNotify = () => {
    if (focusTime) {
      clearTimeout(focusTime);
      focusTime = null;
      console.log("cancel read");
    }
  };

  const handleReadAllNotify = () => {
    readNotifyMutation.mutate(undefined);
  };

  return (
    <StyledWrapper {...props}>
      <StyledHeader>
        <StyledLabel variant={"h2"}>Notifications</StyledLabel>
        <StyledMarkAsRead onClick={handleReadAllNotify}>
          Mark all as read
        </StyledMarkAsRead>
      </StyledHeader>
      <Tabs centered value={tabValue} onChange={handleChangeTab}>
        {tabs.map((tab) => (
          <Tab label={tab.label} value={tab.value} />
        ))}
      </Tabs>
      <StyledNotifyList
        ref={listRef}
        sx={{
          maxHeight: `${window.innerHeight - listTop - 60}px`,
        }}
      >
        {notifyList.map((noti) => (
          <StyledNotifyItem
            variant={"h3"}
            key={noti.id}
            onClick={() => handleReadNotify(noti.id, noti.isRead)}
            onMouseOver={() => handleFocusReadNotify(noti.id, noti.isRead)}
            onMouseOut={handleCancelReadNotify}
            className={noti.isRead ? "read" : ""}
          >
            {noti.message}
          </StyledNotifyItem>
        ))}
      </StyledNotifyList>
    </StyledWrapper>
  );
};

export default Notification;
